import { useState } from "react";
import { toast } from "react-toastify";
import { Ban, Check } from "lucide-react";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";

// Queue chrome lives in the forex feature folder; the KYC page borrows it the
// same way rather than each queue growing its own table.
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { StatusBadge } from "../../features/web3/forex/components/StatusBadge";
import { FormDialog, Field } from "../../features/web3/forex/components/FormDialog";

import { formatDateTime } from "../../lib/format";
import { usePermissions } from "../../hooks/usePermissions";
import {
  useIbRequests,
  useApproveIbRequest,
  useRejectIbRequest,
  STATUS_OPTIONS,
  TYPE_OPTIONS,
  SNAPSHOT_FIELDS,
} from "./api";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none";
const labelCls =
  "block text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const DEFAULT_FILTERS = { status: "pending", type: "" };

const COLUMNS = [
  { label: "Requested" },
  { label: "User" },
  { label: "Type" },
  { label: "Status" },
  { label: "Conditions when raised" },
  { label: "Forex Wallet" },
  { label: "Trade Volume" },
  { label: "Total Balance" },
  { label: "Total Equity" },
  { label: "Free Margin" },
  { label: "MT5 Accounts" },
  { label: "", align: "right", sticky: true },
];

const displayName = (u) =>
  [u?.name, u?.surname].filter(Boolean).join(" ") || "—";

/**
 * The four figures the request was raised against.
 *
 * Only a `levels` request carries a snapshot - an `activate` one has nothing to
 * judge, so its snapshot is null. Shown rather than recomputed on purpose: a
 * request may have sat for weeks and margin moves.
 */
function Snapshot({ snapshot }) {
  if (!snapshot) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {SNAPSHOT_FIELDS.map((f) => {
        const value = Number(snapshot[f.key]);
        const shown = Number.isFinite(value) ? value : 0;
        const met = shown >= f.target;
        return (
          <span key={f.key} className="whitespace-nowrap font-mono text-xs">
            <span className="text-muted-foreground">{f.label} </span>
            <span className={met ? "text-emerald-400" : "text-amber-400"}>
              {f.money ? `$${shown}` : shown}
            </span>
            <span className="text-muted-foreground">
              /{f.money ? `$${f.target}` : f.target}
            </span>
          </span>
        );
      })}
    </div>
  );
}

// What the user holds right now, for an activate decision. Only `activate` rows
// carry `standing`; a levels request is judged on its snapshot, so its cells are
// blank. The Forex wallet is always readable, but the MT5 figures come from the
// mirror - when it does not answer they arrive as zeros with `available: false`,
// which means "unknown", not "holding nothing".
const STANDING_FIELDS = [
  { key: "forexWalletBalance", money: true, mt5: false },
  { key: "totalTradeVolume", suffix: " lots", mt5: true },
  { key: "totalBalance", money: true, mt5: true },
  { key: "totalEquity", money: true, mt5: true },
  { key: "totalFreeMargin", money: true, mt5: true },
  { key: "mt5Accounts", mt5: true },
];

function standingCell(standing, f) {
  if (!standing) return <span className="text-muted-foreground">—</span>;
  if (f.mt5 && !standing.available)
    return <span className="text-xs text-amber-400">Unavailable</span>;
  const value = Number(standing[f.key]) || 0;
  return f.money ? `$${value}` : `${value}${f.suffix || ""}`;
}

export default function IbRequests() {
  const { can } = usePermissions();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const list = useIbRequests(filters);
  const approve = useApproveIbRequest();
  const reject = useRejectIbRequest();

  const [confirming, setConfirming] = useState(null); // { row, action }
  const [note, setNote] = useState("");

  const rows = list.data;
  const busy = approve.isPending || reject.isPending;

  // Deciding and paying are separate permissions by design, so each button is
  // gated on its own verb rather than on reaching the page.
  const canApprove = can("POST:/api/admin/ib/requests/approve");
  const canReject = can("POST:/api/admin/ib/requests/reject");

  const setField = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value }));

  const close = () => {
    setConfirming(null);
    setNote("");
  };

  const run = async (mutation, verb) => {
    try {
      await mutation.mutateAsync({
        requestId: confirming.row.requestId,
        note: note.trim() || undefined,
      });
      toast.success(`Request ${verb}`);
      close();
    } catch (err) {
      // Approving a levels request re-checks the conditions, so "Conditions are
      // no longer met" arrives here as a normal answer rather than a fault. Show
      // the backend wording either way.
      toast.error(err?.response?.data?.message || `${verb} failed`);
      close();
    }
  };

  return (
    <QueueLayout
      eyebrow="Forex IB"
      title="Income"
      accent="requests"
      description="NUs asking to open Forex IB income, or to extend it to levels 2 and 3. Approving also switches on their referral link."
      isFetching={list.isFetching}
      onRefresh={() => list.refetch()}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={labelCls}>Status</label>
          <select
            name="status"
            value={filters.status}
            onChange={setField}
            className={inputCls}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select
            name="type"
            value={filters.type}
            onChange={setField}
            className={inputCls}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <QueueTable
          columns={COLUMNS}
          isLoading={list.isLoading}
          rows={rows}
          renderRow={(row) => (
            <tr
              key={row.requestId}
              className="transition-colors hover:bg-white/[0.02]"
            >
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {formatDateTime(row.createdAt)}
              </Td>
              <Td>
                <div className="font-medium">{displayName(row.user)}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {row.user?.username || "—"}
                </div>
              </Td>
              <Td className="whitespace-nowrap">
                {row.type === "levels" ? "Levels 2 & 3" : "Activate"}
              </Td>
              <Td>
                <StatusBadge status={row.status?.toUpperCase()} />
              </Td>
              <Td>
                <Snapshot snapshot={row.snapshot} />
              </Td>
              {STANDING_FIELDS.map((f) => (
                <Td key={f.key} className="whitespace-nowrap font-mono">
                  {standingCell(row.standing, f)}
                </Td>
              ))}
              <Td align="right" sticky>
                {row.status === "pending" ? (
                  <div className="flex justify-end gap-2">
                    {canApprove && (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => setConfirming({ row, action: "approve" })}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Approve
                      </Button>
                    )}
                    {canReject && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => setConfirming({ row, action: "reject" })}
                      >
                        <Ban className="h-3.5 w-3.5" />
                        Reject
                      </Button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {row.reviewNote || "—"}
                  </span>
                )}
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog
        open={confirming?.action === "approve"}
        onOpenChange={(v) => !v && close()}
        title="Approve Forex IB request"
        description={
          confirming?.row?.type === "levels"
            ? "Opens levels 2 and 3. The conditions are checked again now, so this can still come back short."
            : "Opens Forex IB income and activates this user's referral link."
        }
        onSubmit={() => run(approve, "approved")}
        submitLabel="Approve"
        loading={approve.isPending}
      >
        <Field label="Note (optional)">
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Kept on the request for whoever reads it next"
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={confirming?.action === "reject"}
        onOpenChange={(v) => !v && close()}
        title="Reject Forex IB request"
        description="The user is told their request was not approved, and sees any note you leave."
        onSubmit={() => run(reject, "rejected")}
        submitLabel="Reject"
        loading={reject.isPending}
        danger
      >
        <Field label="Note (optional)">
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shown to the user with the rejection"
          />
        </Field>
      </FormDialog>
    </QueueLayout>
  );
}
